import { Alert, Button, Select, Skeleton } from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import { ErrorScreen } from 'components/ErrorScreen'
import {
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import { getComponentSubtree } from 'features/libra/ontology/ontologyFieldTypes'
import {
  isFullyPinned,
  listComponentInspectionOptions,
  resolvePinnedInspections,
  SELECTED_INSPECTION_KEY,
} from 'features/libra/stepComponents/ffs/inspectionSelection'
import { useLibraAssetModelData } from 'features/libra/stepComponents/useLibraAssetModelData'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { buildFieldValuesPatch } from 'features/libra/ticket/fieldValuesPatch'
import {
  buildInspectionsByComponentId,
  buildInspectionsWithCdfData,
  sortComponentsForTabs,
} from 'features/libra/ticket/selectComponentInspection'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useTicketWriter } from 'features/libra/ticket/TicketWriterContext'
import { LibraWorflowStepComponentProps } from 'features/libra/workflow/workflowStepsApi'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoInspection } from 'types'

/**
 * "Select Inspections" — the first step of the FFS workflow.
 *
 * `TabbedWorkspace` resolves each component's inspection LIVE on every
 * render, so uploading a newer inspection mid-ticket silently swaps the data
 * an in-flight FFS assessment reads. This step makes that choice explicit and
 * pins it: the engineer reviews the auto-recommended inspection per
 * component, may override it, then "Lock in" persists the choice to
 * `fieldValues.components[componentId][SELECTED_INSPECTION_KEY]`. Unit-wide by
 * design (`requiresComponentScope: false` in the workflow entry) -- it reads
 * every component at once.
 */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const Title = styled.h2`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
`

const Blurb = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
`

const RowList = styled.div`
  display: flex;
  flex-direction: column;
`

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
`

const RowControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`

const ComponentName = styled.span`
  font-weight: 600;
  min-width: 12rem;
`

const Muted = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.8125rem;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`

/** Memoized Select options for one component (newest-first, recommended/no-data flagged). */
function useComponentOptions(
  componentId: string,
  inspections: readonly GeckoInspection[],
  inspectionsWithData: Map<string, Set<string>>,
  recommendedId: string | undefined
) {
  return useMemo(
    () =>
      listComponentInspectionOptions(
        componentId,
        inspections,
        inspectionsWithData,
        recommendedId
      ),
    [componentId, inspections, inspectionsWithData, recommendedId]
  )
}

export const Component: FC<
  LibraWorflowStepComponentProps<Record<string, unknown>>
> = ({ readonly = false }) => {
  const {
    components,
    inspections,
    inspectionEntries,
    componentDataFrames,
    componentDatasets,
    isLoadingComponentsAndInspections,
    componentsAndInspectionsError,
    forceRefresh,
  } = useLibraAssetModelData()
  const { fieldValues } = useTicketAccess()
  const { enqueue } = useTicketWriter()
  const [isSaving, setIsSaving] = useState(false)

  const sortedComponents = useMemo(
    () => sortComponentsForTabs(components),
    [components]
  )

  const recommended = useMemo(
    () =>
      buildInspectionsByComponentId(
        sortedComponents,
        inspectionEntries,
        componentDataFrames,
        componentDatasets
      ),
    [
      sortedComponents,
      inspectionEntries,
      componentDataFrames,
      componentDatasets,
    ]
  )
  const inspectionsWithData = useMemo(
    () => buildInspectionsWithCdfData(componentDataFrames, componentDatasets),
    [componentDataFrames, componentDatasets]
  )

  // Read fresh on every render -- writes from "Lock in" land here once the
  // ticket commits, which is what `isFullyPinned` below must react to.
  const persistedSubtree = getComponentSubtree(fieldValues)

  const componentsWithInspections = useMemo(
    () => sortedComponents.filter((c) => c.id in recommended),
    [sortedComponents, recommended]
  )
  const componentsWithoutInspections = useMemo(
    () => sortedComponents.filter((c) => !(c.id in recommended)),
    [sortedComponents, recommended]
  )

  const initialPending = useMemo(
    () =>
      resolvePinnedInspections(
        recommended,
        persistedSubtree,
        sortedComponents,
        inspectionEntries
      ),
    [recommended, persistedSubtree, sortedComponents, inspectionEntries]
  )

  const [pending, setPending] = useState<Record<string, string>>({})

  // Seed each component's draft the first time its resolved default becomes
  // available (async load). Only fills keys the engineer hasn't touched yet,
  // so it never clobbers an in-progress selection.
  useEffect(() => {
    setPending((prev) => {
      const next = { ...prev }
      let changed = false
      for (const [componentId, inspectionId] of Object.entries(
        initialPending
      )) {
        if (next[componentId] === undefined) {
          next[componentId] = inspectionId
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [initialPending])

  const inspectionById = useMemo(
    () => new Map(inspections.map((inspection) => [inspection.id, inspection])),
    [inspections]
  )

  const handleSelect = useCallback(
    (componentId: string, inspectionId: string) => {
      setPending((prev) => ({ ...prev, [componentId]: inspectionId }))
    },
    []
  )

  const handleResetToRecommended = useCallback(
    (componentId: string) => {
      const recommendedId = recommended[componentId]
      if (recommendedId === undefined) return
      setPending((prev) => ({ ...prev, [componentId]: recommendedId }))
    },
    [recommended]
  )

  const handleLockIn = useCallback(async () => {
    setIsSaving(true)
    try {
      await Promise.all(
        componentsWithInspections.map((component) => {
          const inspectionId = pending[component.id]
          if (inspectionId === undefined) return Promise.resolve(null)
          return enqueue((latest) => ({
            fieldValues: buildFieldValuesPatch(
              latest.fieldValues,
              { kind: 'component', componentId: component.id },
              { [SELECTED_INSPECTION_KEY]: wrapFieldValue(inspectionId) }
            ),
          }))
        })
      )
    } finally {
      setIsSaving(false)
    }
  }, [componentsWithInspections, pending, enqueue])

  // Fail closed while the unit's components/inspections are still loading --
  // an empty list would otherwise trivially satisfy `isFullyPinned` and
  // briefly report the step complete before real data arrives.
  const fullyPinned =
    !isLoadingComponentsAndInspections &&
    isFullyPinned(sortedComponents, persistedSubtree, inspectionEntries)

  useReportValidation(
    useMemo(
      () =>
        fullyPinned
          ? undefined
          : {
              status: 'Error' as const,
              message: 'Lock in an inspection for every component to continue.',
            },
      [fullyPinned]
    )
  )

  if (isLoadingComponentsAndInspections) {
    return <Skeleton active paragraph={{ rows: 8 }} />
  }
  if (componentsAndInspectionsError) {
    return (
      <ErrorScreen
        title='Failed to load unit data'
        message={`Could not load this unit's components and inspections from Rolodex: ${componentsAndInspectionsError.message}`}>
        <CantileverButton onClick={forceRefresh}>Retry</CantileverButton>
      </ErrorScreen>
    )
  }

  return (
    <Container>
      <Header>
        <Title>Select Inspections</Title>
        <Blurb>
          Pick the inspection each component&apos;s FFS assessment will use,
          then lock them in. Once locked, the ticket keeps reading these
          inspections for its entire life -- uploading a newer one will not
          silently swap the data.
        </Blurb>
      </Header>

      <RowList>
        {componentsWithInspections.map((component) => (
          <ComponentInspectionRow
            key={component.id}
            componentId={component.id}
            componentName={component.data.name || component.id}
            inspections={inspections}
            inspectionsWithData={inspectionsWithData}
            recommendedId={recommended[component.id]}
            persistedId={unwrapFieldValue<string>(
              persistedSubtree?.[component.id]?.[SELECTED_INSPECTION_KEY]
            )}
            inspectionById={inspectionById}
            value={pending[component.id]}
            readonly={readonly}
            onSelect={handleSelect}
            onReset={handleResetToRecommended}
          />
        ))}
        {componentsWithoutInspections.map((component) => (
          <Row key={component.id}>
            <RowControls>
              <ComponentName>
                {component.data.name || component.id}
              </ComponentName>
              <Muted>No inspections -- excluded from FFS</Muted>
            </RowControls>
          </Row>
        ))}
      </RowList>

      <Footer>
        <Button
          type='primary'
          loading={isSaving}
          disabled={readonly || componentsWithInspections.length === 0}
          onClick={handleLockIn}>
          Lock in inspections
        </Button>
      </Footer>
    </Container>
  )
}

/** One component's row: inspection picker, reset action, and the stale-pin notice. */
const ComponentInspectionRow: FC<{
  componentId: string
  componentName: string
  inspections: readonly GeckoInspection[]
  inspectionsWithData: Map<string, Set<string>>
  recommendedId: string | undefined
  persistedId: string | undefined
  inspectionById: Map<string, GeckoInspection>
  value: string | undefined
  readonly: boolean
  onSelect: (componentId: string, inspectionId: string) => void
  onReset: (componentId: string) => void
}> = ({
  componentId,
  componentName,
  inspections,
  inspectionsWithData,
  recommendedId,
  persistedId,
  inspectionById,
  value,
  readonly,
  onSelect,
  onReset,
}) => {
  const options = useComponentOptions(
    componentId,
    inspections,
    inspectionsWithData,
    recommendedId
  )
  const selectOptions = useMemo(
    () =>
      options.map((option) => ({
        value: option.inspectionId,
        label: [
          option.label,
          option.isRecommended ? '(recommended)' : null,
          !option.hasData ? '(no data)' : null,
        ]
          .filter(Boolean)
          .join(' '),
      })),
    [options]
  )

  // A newer inspection than the one this component is PINNED to (not the
  // in-progress draft) is a non-blocking notice, not a re-open -- see the
  // plan's "Reopen semantics" section.
  const persistedInspection = persistedId
    ? inspectionById.get(persistedId)
    : undefined
  const newestInspection = options[0]
    ? inspectionById.get(options[0].inspectionId)
    : undefined
  const hasNewerInspection =
    !!persistedInspection &&
    !!newestInspection &&
    newestInspection.id !== persistedInspection.id &&
    newestInspection.data.date.getTime() >
      persistedInspection.data.date.getTime()

  return (
    <Row>
      <RowControls>
        <ComponentName>{componentName}</ComponentName>
        <Select
          aria-label={`Inspection for ${componentName}`}
          style={{ minWidth: '20rem' }}
          value={value}
          onChange={(inspectionId) => onSelect(componentId, inspectionId)}
          disabled={readonly}
          options={selectOptions}
        />
        <Button
          size='small'
          disabled={readonly || recommendedId === undefined}
          onClick={() => onReset(componentId)}>
          Reset to recommended
        </Button>
      </RowControls>
      {hasNewerInspection && (
        <Alert
          type='info'
          showIcon
          title={`A newer inspection is available; pinned to ${persistedInspection.data.date.toISOString().slice(0, 10)}. Re-lock to use it.`}
        />
      )}
    </Row>
  )
}
